import React, { useEffect, useState } from 'react';
import { UniversalLink } from '@plone/volto/components';
import { withBlockExtensions } from '@plone/volto/helpers';
import {
  MPFButton,
  MPFSectionHeading,
  NewsCardRow,
} from 'volto-ministerio-publico-federal/components/UI';

import { DEFAULT_LIMIT } from './schema';
import type { NewsByThemeData } from './schema';
import { DEFAULT_BUTTON_LABEL, resolveButtonHref } from './helpers';
import { useNewsByTheme } from './useNewsByTheme';

interface ViewProps {
  data: NewsByThemeData;
  block: string;
  isEditMode?: boolean;
  /**
   * `@id` de uma notícia a remover da lista. O bloco inserido pelo editor não
   * passa nada; quem passa é a página de notícia, para ela não se listar a si
   * mesma na seção "Mais sobre <tema>".
   */
  excludeId?: string;
  /**
   * Avisa quem embute o bloco sobre os `@id` efetivamente listados. A página de
   * notícia usa isso para o card "Mais lidas" não repetir o que já está no
   * "Veja também".
   */
  onItemsRendered?: (ids: string[]) => void;
  /** Repassa para os cards: `false` esconde a unidade (uso na página de notícia). */
  showUnit?: boolean;
  /**
   * Texto do cabeçalho. O bloco standalone usa o default; a `NewsView` passa
   * "Veja também" ao reaproveitar o bloco no rodapé da notícia.
   */
  sectionHeading?: string;
  /**
   * Prefixo BEM dos cards. O default (`undefined`) deixa o `NewsCardRow` usar o
   * card grande padrão (`mpf-news-card-row`); a `NewsView` passa
   * `mpf-news__related` para o formato reduzido do "Veja também", cujos estilos
   * vivem escopados em `.mpf-news` (`_news-view.scss`).
   */
  cardBaseClass?: string;
}

const ROOT_CLASS = 'mpf-news-by-theme not-prose';

/**
 * Texto do cabeçalho da seção, fixo por decisão do design.
 *
 * O campo "Título da seção" da barra lateral decide apenas SE o cabeçalho
 * aparece — o texto não vem dele. Idem para o `data.title` que a `NewsView`
 * monta ao reaproveitar este bloco no rodapé da notícia.
 */
const SECTION_HEADING = 'Notícias relacionadas';

const useHasMounted = (): boolean => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return hasMounted;
};

const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mpf-news-by-theme__placeholder">{children}</p>
);

const LoadingState: React.FC = () => (
  <div
    className="mpf-news-by-theme__loading"
    aria-busy="true"
    aria-live="polite"
  >
    <span className="sr-only">Carregando notícias…</span>
    <span
      aria-hidden="true"
      className="mpf-loading mpf-loading-spinner mpf-loading-lg text-primary dark:text-night-100"
    />
  </div>
);

export const MpfNewsByThemeBlockView: React.FC<ViewProps> = ({
  data,
  block,
  isEditMode,
  excludeId,
  onItemsRendered,
  showUnit,
  sectionHeading = SECTION_HEADING,
  cardBaseClass,
}) => {
  const limit = Number(data?.limit) || DEFAULT_LIMIT;
  const hasMounted = useHasMounted();

  const { items, loading, loaded } = useNewsByTheme({
    tema: data?.tema,
    unidade: data?.unidade,
    limit,
    blockId: block,
    excludeId,
  });

  const listados = items.slice(0, limit);
  // `join` como dependência: `listados` é um array novo a cada render, e o
  // efeito precisa disparar só quando os ids mudam de fato.
  const listadosKey = listados.map((item) => item['@id']).join('|');
  useEffect(() => {
    onItemsRendered?.(listadosKey ? listadosKey.split('|') : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listadosKey]);

  const { href, external } = resolveButtonHref(data);
  const buttonLabel = data?.buttonLabel || DEFAULT_BUTTON_LABEL;
  const heading = data?.title && <MPFSectionHeading title={sectionHeading} />;

  if (!data?.tema) {
    return isEditMode ? (
      <div className={ROOT_CLASS}>
        <Notice>
          Escolha um tema na barra lateral para listar as últimas notícias.
        </Notice>
      </div>
    ) : null;
  }

  if (loading || !loaded) {
    return (
      <div className={ROOT_CLASS}>
        {heading}
        <LoadingState />
      </div>
    );
  }

  if (items.length === 0) {
    return isEditMode ? (
      <div className={ROOT_CLASS}>
        {heading}
        <Notice>
          Nenhuma notícia publicada foi encontrada para este tema.
        </Notice>
      </div>
    ) : null;
  }

  return (
    <section className={ROOT_CLASS}>
      {heading}

      <div className="mpf-news-by-theme__body">
        {listados.map((item) => (
          <NewsCardRow
            key={item['@id']}
            item={item}
            baseClass={cardBaseClass}
            showRelativeDate={hasMounted}
            unitContext={data?.unidade}
            showUnit={showUnit}
          />
        ))}
      </div>

      <div className="mpf-news-by-theme__footer">
        <MPFButton
          as={UniversalLink}
          href={href}
          openLinkInNewTab={external}
          variant="primary"
          size="xl"
          fullWidth
        >
          {buttonLabel}
        </MPFButton>
      </div>
    </section>
  );
};

export default withBlockExtensions(MpfNewsByThemeBlockView);
